import { v4 as uuidv4 } from "uuid"
import { PROCESS_STEP_RESPONSE_TYPE } from "../data"

export const mapToIds = (collection) => collection?.map((item) => toId(item)) || []

export const toId = (item, allowNull) => (!item && allowNull ? null : item?._id || item?.id || item)

export const toRef = (item, allowNull) => (!item && allowNull ? null : item?.__ref)

export const arraysMatch = (a, b) => a.every((item) => b.includes(item)) && b.every((item) => a.includes(item))

export const toIntArray = (collection) => collection.map((item) => Number.parseInt(item))

export const makeTemporaryId = () => `temp-${uuidv4()}`

export const isTemporaryId = (id) => id?.toString().startsWith("temp-")

export const mapRemoveTemporaryIds = (collection) =>
  collection.map(({ id, ...item }) => (isTemporaryId(id) ? { ...item } : { ...item, id }))

export const formatEnumKeyForDisplay = (key) => {
  return key.replace(/_/g, " ").toLowerCase()
}

export const joinStringsWithNaturalLanguage = (arrayOfStrings, conjunction = "and") =>
  arrayOfStrings.join(", ").replace(/, ([^,]*)$/, ` ${conjunction} $1`)

export const valueByDotNotation = (path, parent) => {
  if (path.includes(".")) {
    return path.split(".").reduce(
      (acc, propName) => {
        acc.value = acc.parent[propName]
        acc.parent = acc.value
        return acc
      },
      { parent, value: null }
    ).value
  }
  return parent[path]
}

// TODO: replace - apollo client 3.8.0 should have middleware to
// remove __typename for mutation inputs
export const deepRemoveTypenames = (obj) => deepOmit(obj, ["__typename"])

// TODO: replace - apollo client 3.8.0 should have middleware to
// remove __typename for mutation inputs
export const removeTypename = (prev) => {
  const value = prev ? { ...prev } : prev
  if (value?.__typename) {
    delete value.__typename
  }
  return value
}

// recursively iterates through an object or
// array and omits the specified keys
export const deepOmit = (obj, keysToOmit) => {
  const omitKeys = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => omitKeys(item))
    }
    if (typeof value === "object" && value !== null) {
      return Object.keys(value).reduce((acc, key) => {
        if (!keysToOmit.includes(key)) {
          acc[key] = omitKeys(value[key])
        }
        return acc
      }, {})
    }
    return value
  }
  return omitKeys(obj)
}

export const hasIterator = (obj) => {
  // checks for null and undefined
  if (obj == null) {
    return false
  }
  return typeof obj[Symbol.iterator] === "function"
}

export const isLastInArray = (array, index) => index === array.length - 1

export const isArrayOddLength = (array) => array.length % 2 > 0

export const listHasSomeById = (list1, list2) =>
  list1.some((item1) => list2.find((item2) => toId(item2) === toId(item1)))

export const chunkArray = (array, chunkSize) =>
  Array(Math.ceil(array.length / chunkSize))
    .fill()
    .map((_, index) => index * chunkSize)
    .map((begin) => array.slice(begin, begin + chunkSize))

export const multipleSelectChange = (prev, eventOrValues) => {
  const targetValues = eventOrValues.target?.value || eventOrValues
  if (prev.includes("all") && targetValues.length > 1) {
    return targetValues.filter((targetValue) => targetValue !== "all")
  }
  if ((!prev.includes("all") && targetValues.includes("all")) || !targetValues.length) {
    return ["all"]
  }
  return targetValues
}

export const convertCelsius = (celsius, displayUnit) => (displayUnit === "f" ? (celsius * 9) / 5 + 32 : celsius)

export const filterObjectKeys = (obj, keysToRemove) => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => !keysToRemove.includes(key)))
}

export const formatTemperatureInfo = (temperatureInfo, emptyValue = "") => {
  if (!temperatureInfo?.value) {
    return emptyValue
  }

  const { value, displayUnit } = temperatureInfo

  return `${convertCelsius(value, displayUnit).toFixed(1)} ${displayUnit === "f" ? "°F" : "°C"}`
}

export const formatHumidityInfo = (humidityInfo, emptyValue = "") => {
  if (!humidityInfo?.value) {
    return emptyValue
  }

  return `${humidityInfo.value.toFixed(1)} %RH`
}

/**
 * Shuffles an array in a random order using the Fisher-Yates algorithm.
 * This function does not mutate the original array.
 * @see {@link https://en.wikipedia.org/wiki/Fisher-Yates_shuffle|Fisher-Yates Shuffle on Wikipedia}
 * @param {Array} array The array to shuffle.
 * @returns {Array} A new array with the elements in a random order.
 */
export const shuffleArray = (array) => {
  // 1. Create a copy of the array to avoid modifying the original
  const newArray = [...array]

  // 2. Loop backwards through the array
  for (let index = newArray.length - 1; index > 0; index--) {
    // 3. Pick a random index from the remaining elements (0 to i)
    const randomIndex = Math.floor(Math.random() * (index + 1))

    // 4. Swap the current element with the randomly picked element using array destructuring
    ;[newArray[index], newArray[randomIndex]] = [newArray[randomIndex], newArray[index]]
  }

  return newArray
}

/**
 * Compares two objects for deep equality using JSON serialization.
 * Handles null, undefined, and complex nested objects/arrays.
 * @param {any} obj1 - First object to compare
 * @param {any} obj2 - Second object to compare
 * @returns {boolean} True if objects are deeply equal, false otherwise
 */
export const objectsMatch = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}

export const isArrayOf = (array, type) => {
  if (!Array.isArray(array)) return false

  if (typeof type === "string") {
    const checkers = {
      string: (v) => typeof v === "string",
      number: (v) => typeof v === "number",
      boolean: (v) => typeof v === "boolean",
      object: (v) => typeof v === "object" && v !== null && !Array.isArray(v),
      array: (v) => Array.isArray(v),
      function: (v) => typeof v === "function",
      undefined: (v) => typeof v === "undefined",
      symbol: (v) => typeof v === "symbol",
      bigint: (v) => typeof v === "bigint"
    }

    const checker = checkers[type]

    if (!checker) return false

    return array.every(checker)
  }

  if (typeof type === "function") {
    return array.every((item) => item instanceof type)
  }

  return false
}

export function duplicateJobStep(originalStep, overrides) {
  const newStep = {
    ...originalStep,
    completedAt: null,
    completedBy: null,
    skip: false,
    skippedInBatch: false,
    notApplicable: false,
    notes: [],
    ...overrides
  }

  // NOTE(ando): omitted types just fall through default
  switch (newStep.responseType) {
    case PROCESS_STEP_RESPONSE_TYPE.DATE_TIME:
    case PROCESS_STEP_RESPONSE_TYPE.LONG_TEXT:
    case PROCESS_STEP_RESPONSE_TYPE.TEXT:
    case PROCESS_STEP_RESPONSE_TYPE.SIGNATURE: {
      return { ...newStep, response: null }
    }

    case PROCESS_STEP_RESPONSE_TYPE.FILES:
    case PROCESS_STEP_RESPONSE_TYPE.PHOTOS: {
      return { ...newStep, responseUploads: [] }
    }

    case PROCESS_STEP_RESPONSE_TYPE.MULTIPLE_CHOICE: {
      return { ...newStep, response: null, failed: false, score: 0 }
    }

    case PROCESS_STEP_RESPONSE_TYPE.SELECTION: {
      return { ...newStep, responseSelections: [], responseCustomSelections: [], responseFoodItemBatch: null }
    }

    case PROCESS_STEP_RESPONSE_TYPE.TEMPERATURE_TIMER: {
      return {
        ...newStep,
        responseSensors: [],
        responseSelections: [],
        responseTemperatureTimer: [],
        startedAt: null,
        startedBy: null,
        stoppedAt: null,
        stoppedBy: null
      }
    }

    default: {
      return newStep
    }
  }
}
